/* eslint-disable react/jsx-max-props-per-line */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { Document, Page, pdfjs } from 'react-pdf';
import fileDownload from 'js-file-download';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const MIN_SCALE = 0.25;
const MAX_SCALE = 5;
const ZOOM_STEP = 0.25;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const initialTransform = () => ({
  scale: 1,
  rotate: 0,
  x: 0,
  y: 0,
});

const resolveDownloadName = (fileName, title) => {
  const base = (fileName || title || 'documento').trim() || 'documento';
  return base.toLowerCase().endsWith('.pdf') ? base : `${base}.pdf`;
};

const ToolButton = ({ title, onClick, disabled, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        onClick={onClick}
        disabled={disabled}
        size="small"
        aria-label={title}
        sx={{
          color: 'common.white',
          bgcolor: (theme) => alpha(theme.palette.common.white, 0.12),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.common.white, 0.22),
          },
          '&.Mui-disabled': {
            color: (theme) => alpha(theme.palette.common.white, 0.28),
          },
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

ToolButton.propTypes = {
  title: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

const PdfPreviewPanel = ({
  file,
  title,
  fileName,
  enableKeyboard,
  enableArrowKeys,
  showPageArrows,
  showCaption,
  showDownload,
}) => {
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [transform, setTransform] = useState(initialTransform);
  const [pageWidth, setPageWidth] = useState(560);
  const [loadError, setLoadError] = useState(false);

  const resetTransform = useCallback(() => {
    setTransform(initialTransform());
    dragRef.current = null;
  }, []);

  useEffect(() => {
    setNumPages(null);
    setCurrentPage(1);
    setLoadError(false);
    resetTransform();
  }, [file, resetTransform]);

  useEffect(() => {
    setTransform((prev) => ({ ...prev, x: 0, y: 0 }));
    dragRef.current = null;
  }, [currentPage]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const availableWidth = el.clientWidth - 32;
      const availableHeight = el.clientHeight - 112;
      if (availableWidth <= 0 || availableHeight <= 0) return;
      const fit = Math.min(availableWidth, availableHeight / 1.35);
      setPageWidth(Math.max(200, Math.floor(fit)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [file]);

  const zoomBy = useCallback((delta) => {
    setTransform((prev) => ({
      ...prev,
      scale: clamp(Number((prev.scale + delta).toFixed(2)), MIN_SCALE, MAX_SCALE),
    }));
  }, []);

  const goPrev = useCallback(() => {
    setCurrentPage((page) => Math.max(1, page - 1));
  }, []);

  const goNext = useCallback(() => {
    setCurrentPage((page) => (numPages ? Math.min(numPages, page + 1) : page));
  }, [numPages]);

  useEffect(() => {
    if (!enableKeyboard || !file) return undefined;
    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;
      if (enableArrowKeys && event.key === 'ArrowLeft') goPrev();
      if (enableArrowKeys && event.key === 'ArrowRight') goNext();
      if (event.key === '+' || event.key === '=') zoomBy(ZOOM_STEP);
      if (event.key === '-' || event.key === '_') zoomBy(-ZOOM_STEP);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enableKeyboard, enableArrowKeys, file, goPrev, goNext, zoomBy]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || !file) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      zoomBy(event.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [file, zoomBy]);

  const handleDownload = useCallback(async () => {
    if (!file) return;
    const name = resolveDownloadName(fileName, title);
    if (file instanceof Blob) {
      fileDownload(file, name);
      return;
    }
    const url = typeof file === 'string' ? file : file?.url;
    if (!url) return;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      fileDownload(blob, name);
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [file, fileName, title]);

  const onPointerDown = (event) => {
    if (transform.scale <= 1) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      ox: transform.x,
      oy: transform.y,
    };
  };

  const onPointerMove = (event) => {
    if (!dragRef.current) return;
    const drag = dragRef.current;
    setTransform((prev) => ({
      ...prev,
      x: drag.ox + (event.clientX - drag.x),
      y: drag.oy + (event.clientY - drag.y),
    }));
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const pageTransform = `translate(${transform.x}px, ${transform.y}px) rotate(${transform.rotate}deg) scale(${transform.scale})`;
  const canGoPrev = currentPage > 1;
  const canGoNext = Boolean(numPages) && currentPage < numPages;
  const canDownload = file instanceof Blob || typeof file === 'string' || Boolean(file?.url);

  const sideSx = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
  };

  return (
    <Box
      ref={stageRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        bgcolor: 'common.black',
        userSelect: 'none',
      }}
    >
      {!file ? (
        <Typography variant="body2" sx={{ color: (theme) => alpha(theme.palette.common.white, 0.72) }}>
          Nenhum arquivo PDF selecionado.
        </Typography>
      ) : (
        <Document
          file={file}
          onLoadSuccess={({ numPages: total }) => {
            setLoadError(false);
            setNumPages(total);
            setCurrentPage(1);
          }}
          onLoadError={() => setLoadError(true)}
          loading={(
            <Typography variant="body2" sx={{ color: (theme) => alpha(theme.palette.common.white, 0.72) }}>
              Carregando PDF...
            </Typography>
          )}
          error={(
            <Typography variant="body2" sx={{ color: 'error.light', px: 2, textAlign: 'center' }}>
              Não foi possível carregar o PDF.
            </Typography>
          )}
        >
          {!loadError ? (
            <Box
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
              sx={{
                cursor: transform.scale > 1 ? 'grab' : 'default',
                transform: pageTransform,
                transformOrigin: 'center center',
                touchAction: 'none',
                '& .react-pdf__Page': {
                  bgcolor: 'transparent',
                },
                '& .react-pdf__Page__canvas': {
                  display: 'block',
                },
              }}
            >
              <Page
                pageNumber={currentPage}
                width={pageWidth}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Box>
          ) : null}
        </Document>
      )}

      {showPageArrows && numPages > 1 ? (
        <>
          <Box sx={{ ...sideSx, left: 8 }}>
            <ToolButton title="Página anterior" onClick={goPrev} disabled={!canGoPrev}>
              <NavigateBeforeIcon fontSize="medium" />
            </ToolButton>
          </Box>
          <Box sx={{ ...sideSx, right: 8 }}>
            <ToolButton title="Próxima página" onClick={goNext} disabled={!canGoNext}>
              <NavigateNextIcon fontSize="medium" />
            </ToolButton>
          </Box>
        </>
      ) : null}

      <Stack
        spacing={1}
        alignItems="center"
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 16,
          px: 2,
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            pointerEvents: 'auto',
            px: 1,
            py: 0.75,
            borderRadius: 8,
            bgcolor: (theme) => alpha(theme.palette.common.black, 0.55),
          }}
        >
          <ToolButton title="Diminuir zoom" onClick={() => zoomBy(-ZOOM_STEP)} disabled={!file || loadError}>
            <ZoomOutIcon fontSize="small" />
          </ToolButton>
          <ToolButton title="Aumentar zoom" onClick={() => zoomBy(ZOOM_STEP)} disabled={!file || loadError}>
            <ZoomInIcon fontSize="small" />
          </ToolButton>
          <ToolButton title="Ajustar à tela" onClick={resetTransform} disabled={!file || loadError}>
            <FitScreenIcon fontSize="small" />
          </ToolButton>
          <ToolButton
            title="Girar à esquerda"
            disabled={!file || loadError}
            onClick={() => setTransform((prev) => ({ ...prev, rotate: prev.rotate - 90 }))}
          >
            <RotateLeftIcon fontSize="small" />
          </ToolButton>
          <ToolButton
            title="Girar à direita"
            disabled={!file || loadError}
            onClick={() => setTransform((prev) => ({ ...prev, rotate: prev.rotate + 90 }))}
          >
            <RotateRightIcon fontSize="small" />
          </ToolButton>
          {!showPageArrows && numPages > 1 ? (
            <>
              <ToolButton title="Página anterior" onClick={goPrev} disabled={!canGoPrev}>
                <NavigateBeforeIcon fontSize="small" />
              </ToolButton>
              <Typography
                variant="caption"
                sx={{ color: 'common.white', minWidth: 42, textAlign: 'center', alignSelf: 'center' }}
              >
                {`${currentPage}/${numPages}`}
              </Typography>
              <ToolButton title="Próxima página" onClick={goNext} disabled={!canGoNext}>
                <NavigateNextIcon fontSize="small" />
              </ToolButton>
            </>
          ) : null}
          {showDownload ? (
            <ToolButton title="Baixar" onClick={handleDownload} disabled={!canDownload}>
              <CloudDownloadIcon fontSize="small" />
            </ToolButton>
          ) : null}
        </Stack>
        {showCaption && file ? (
          <Stack spacing={0.25} alignItems="center" sx={{ maxWidth: '90%' }}>
            <Typography variant="body2" sx={{ color: 'common.white', fontWeight: 600, textAlign: 'center' }}>
              {title || 'Documento PDF'}
            </Typography>
            <Typography variant="caption" sx={{ color: (theme) => alpha(theme.palette.common.white, 0.7) }}>
              {numPages ? `${currentPage} / ${numPages}` : '—'}
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
};

PdfPreviewPanel.propTypes = {
  file: PropTypes.any,
  title: PropTypes.string,
  fileName: PropTypes.string,
  enableKeyboard: PropTypes.bool,
  enableArrowKeys: PropTypes.bool,
  showPageArrows: PropTypes.bool,
  showCaption: PropTypes.bool,
  showDownload: PropTypes.bool,
};

PdfPreviewPanel.defaultProps = {
  file: null,
  title: '',
  fileName: '',
  enableKeyboard: true,
  enableArrowKeys: true,
  showPageArrows: true,
  showCaption: true,
  showDownload: true,
};

export default PdfPreviewPanel;
